import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { supabase } from '@/integrations/supabase/client';
import React from 'react';

interface AcademicYear {
  year: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_application_year?: boolean;
  application_start_date?: string;
  application_end_date?: string;
  created_at: string;
  updated_at: string;
}

interface GetAcademicYearsParams {
  status?: 'active' | 'inactive';
  includeStats?: boolean;
}

interface CreateAcademicYearData {
  year: string;
  start_date: string;
  end_date: string;
  is_active?: boolean;
}

interface UpdateAcademicYearData {
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
}

// Query keys factory
export const academicYearKeys = {
  all: ['academicYears'] as const,
  lists: () => [...academicYearKeys.all, 'list'] as const,
  list: (params?: GetAcademicYearsParams) => [...academicYearKeys.lists(), params] as const,
  active: () => [...academicYearKeys.all, 'active'] as const,
  detail: (year: string) => [...academicYearKeys.all, year] as const,
};

// Direct Supabase API functions
const api = {
  async getAll(params?: GetAcademicYearsParams) {
    try {
      let query = supabase
        .from('academic_years')
        .select('*')
        .order('year', { ascending: false });
      
      // Apply filters
      if (params?.status === 'active') {
        query = query.eq('is_active', true);
      } else if (params?.status === 'inactive') {
        query = query.eq('is_active', false);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching academic years:', error);
        throw new Error('Failed to fetch academic years');
      }
      
      // Get additional stats if requested
      let stats = {};
      if (params?.includeStats && data && data.length > 0) {
        const activeYear = data.find(year => year.is_active);
        if (activeYear) {
          const { count: totalApplications } = await supabase
            .from('applications')
            .select('*', { count: 'exact', head: true })
            .eq('academic_year', activeYear.year);
          
          stats = {
            activeYear: activeYear.year,
            totalApplications: totalApplications || 0
          };
        }
      }
      
      return {
        data: data || [],
        meta: {
          total: data?.length || 0,
          ...stats
        }
      };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },

  async getActive() {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('is_active', true)
        .order('year', { ascending: false });

      if (error) {
        console.error('Error fetching active years:', error);
        throw new Error('Failed to fetch active years');
      }

      const today = new Date().toISOString().split('T')[0];

      // Determine current year with priority:
      // 1. Manual override (is_application_year = true)
      // 2. Application period dates (Oct-Sept for applications)
      // 3. School year dates (July-June for ongoing school year)
      // 4. First active year as fallback

      // Priority 1: Check for manual override
      let currentYear = data?.find(year => year.is_application_year === true);

      // Priority 2: Check application period dates
      if (!currentYear) {
        currentYear = data?.find(year =>
          year.application_start_date &&
          year.application_end_date &&
          year.application_start_date <= today &&
          year.application_end_date >= today
        );
      }

      // Priority 3: Check school year dates (ongoing school year)
      if (!currentYear) {
        currentYear = data?.find(year =>
          year.is_active &&
          year.start_date <= today &&
          year.end_date >= today
        );
      }

      // Priority 4: Fallback to first active year
      if (!currentYear) {
        currentYear = data?.find(year => year.is_active);
      }

      return {
        data: {
          active: data || [],
          current: currentYear || null
        }
      };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },

  async getByYear(year: string) {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('year', year)
        .single();
      
      if (error) {
        console.error('Error fetching academic year:', error);
        throw new Error('Failed to fetch academic year');
      }
      
      return { data };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },

  async create(data: CreateAcademicYearData) {
    try {
      // Check if year already exists
      const { data: existing } = await supabase
        .from('academic_years')
        .select('year')
        .eq('year', data.year)
        .single();
      
      if (existing) {
        throw new Error('Academic year already exists');
      }
      
      // Insert new academic year
      const { data: newYear, error } = await supabase
        .from('academic_years')
        .insert({
          year: data.year,
          start_date: data.start_date,
          end_date: data.end_date,
          is_active: data.is_active || false
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error creating academic year:', error);
        throw new Error('Failed to create academic year');
      }
      
      return { data: newYear };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },

  async update(year: string, data: UpdateAcademicYearData) {
    try {
      const { data: updatedYear, error } = await supabase
        .from('academic_years')
        .update({
          ...(data.start_date && { start_date: data.start_date }),
          ...(data.end_date && { end_date: data.end_date }),
          ...(data.is_active !== undefined && { is_active: data.is_active }),
          updated_at: new Date().toISOString()
        })
        .eq('year', year)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating academic year:', error);
        throw new Error('Failed to update academic year');
      }
      
      return { data: updatedYear };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },

  async activate(year: string, previousActiveYear?: string) {
    try {
      // Deactivate all years first
      const { error: deactivateError } = await supabase
        .from('academic_years')
        .update({ is_active: false })
        .neq('year', year);
      
      if (deactivateError) {
        console.error('Error deactivating years:', deactivateError);
        throw new Error('Failed to deactivate years');
      }
      
      // Activate the specified year
      const { data: activatedYear, error: activateError } = await supabase
        .from('academic_years')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('year', year)
        .select()
        .single();
      
      if (activateError) {
        console.error('Error activating year:', activateError);
        throw new Error('Failed to activate academic year');
      }
      
      return { 
        data: activatedYear,
        message: 'Academic year activated successfully',
        warnings: []
      };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },

  async delete(year: string) {
    try {
      const { error } = await supabase
        .from('academic_years')
        .delete()
        .eq('year', year);
      
      if (error) {
        console.error('Error deleting academic year:', error);
        throw new Error('Failed to delete academic year');
      }
      
      return { message: 'Academic year deleted successfully' };
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  },
};

// Cache configuration
const CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const STALE_TIME = 2 * 60 * 1000; // 2 minutes

// React Query hooks
export function useAcademicYears(params?: GetAcademicYearsParams) {
  return useQuery({
    queryKey: academicYearKeys.list(params),
    queryFn: () => api.getAll(params),
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function useActiveAcademicYears() {
  const query = useQuery({
    queryKey: academicYearKeys.active(),
    queryFn: api.getActive,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    retry: 3,
  });

  // Cache in localStorage for offline support
  React.useEffect(() => {
    if (query.data?.data?.active) {
      localStorage.setItem('cached_academic_years', JSON.stringify(query.data.data.active));
      localStorage.setItem('cached_academic_years_timestamp', Date.now().toString());
    }
  }, [query.data]);

  // Use cached data when there's an error
  React.useEffect(() => {
    if (query.error) {
      const cached = localStorage.getItem('cached_academic_years');
      const cachedTimestamp = localStorage.getItem('cached_academic_years_timestamp');
      
      if (cached && cachedTimestamp) {
        const age = Date.now() - parseInt(cachedTimestamp);
        if (age < 24 * 60 * 60 * 1000) { // 24 hours
          // Cached data is available
        }
      }
    }
  }, [query.error]);

  return query;
}

export function useAcademicYear(year: string) {
  return useQuery({
    queryKey: academicYearKeys.detail(year),
    queryFn: () => api.getByYear(year),
    enabled: !!year,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
}

export function useCreateAcademicYear() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicYearKeys.all });
      toast.success('Academic year created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create academic year');
    },
  });
}

export function useUpdateAcademicYear() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ year, data }: { year: string; data: UpdateAcademicYearData }) =>
      api.update(year, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: academicYearKeys.all });
      queryClient.invalidateQueries({ queryKey: academicYearKeys.detail(variables.year) });
      toast.success('Academic year updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update academic year');
    },
  });
}

export function useActivateAcademicYear() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ year, previousActiveYear }: { year: string; previousActiveYear?: string }) =>
      api.activate(year, previousActiveYear),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: academicYearKeys.all });
      
      // Show warnings if any
      if (data.warnings && data.warnings.length > 0) {
        data.warnings.forEach((warning: any) => {
          toast(warning.message, { icon: '⚠️' });
        });
      }
      
      toast.success(data.message || 'Academic year activated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to activate academic year');
    },
  });
}

export function useDeleteAcademicYear() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicYearKeys.all });
      toast.success('Academic year deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete academic year');
    },
  });
}

// Helper hook to get academic years as simple string array
export function useAcademicYearsList() {
  const { data, ...rest } = useActiveAcademicYears();
  
  const academicYearsData = data as { data: { active: AcademicYear[] } } | undefined;
  const years = academicYearsData?.data?.active?.map((year) => year.year) || [];
  
  return {
    data: years,
    ...rest,
  };
}